import { Grid } from "@mui/material";
import DyanamicDropdown from "components/Elements/DynamicDropdown";
import SiteAssetRegisterDropdown from "components/Elements/SiteAssetRegisterDropdown";
import TextAreaInputField from "components/Elements/TextAreaInputField";
import AccordionBox from "components/Layouts/AccordionBox";
import { debounce, getLocalStorageData } from "helpers/utils";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { appPath, modelTask, modelsPath } from "helpers/routePaths";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { showError } from "../../../../redux/common/actions";
import { getFeedbackClassifications } from "services/clients/sites/siteApplications/feedbackClassifications";
import { getFeedbackPriorities } from "services/clients/sites/siteApplications/feedbackPriorities";
import { getPositions } from "services/clients/sites/siteApplications/userPositions";
import {
	getSiteAssetRegistersLookup,
	getSiteAssetRegistersLookupCount,
} from "services/clients/sites/siteAssetRegisters";
import {
	getFeedbackAvailableUsers,
	getFeedbackTasks,
	updateFeedback,
} from "services/feedback/feedbackdetails";
import { getModelAvailableAsset } from "services/models/modelDetails/modelAsset";
import { getModelIntervals } from "services/models/modelDetails/modelIntervals";
import { getModelRolesListByInterval } from "services/models/modelDetails/modelRoles";
import { getModelStage } from "services/models/modelDetails/modelStages";
import { getSingleModelTask } from "services/models/modelDetails/modelTasks";
import { getModelZonesList } from "services/models/modelDetails/modelZones";
import { getPublishedModel } from "services/models/modelList";
import { getSiteDepartmentsInService } from "services/services/serviceLists";
import AddDialogStyle from "styles/application/AddDialogStyle";

const Add = AddDialogStyle();

const dropdownIdNames = {
	department: "siteDepartmentID",
	position: "assignPositionID",
	user: "assignUserID",
	classification: "feedbackClassificationID",
	priority: "feedbackPriorityID",
	asset: "siteAssetRegisterID",
	model: "modelID",
	stage: "ModelVersionStageID",
	zone: "ModelVersionZoneID",
	task: "ModelVersionTaskID",
	interval: "ModelVersionIntervalID",
	role: "ModelVersionRoleID",
};

function Details({
	details,
	siteAppID,
	siteId,
	captions,
	feedbackId,
	isReadOnly,
}) {
	const [selectedDropdown, setSelectedDropdown] = useState({
		department: {},
		riskRating: {},
	});
	const [input, setInput] = useState("");
	const [isInputChanged, setIsInputChanged] = useState(false);
	const [page, setPage] = useState({ pageNo: 1, pageSize: 12 });
	const [siteAsset, setSiteAsset] = useState([]);
	const [count, setCount] = useState(null);
	const [positionUsers, setPositionUsers] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [availableModel, setAvailableModel] = useState([]);
	const [isNavigatingToTask, setIsNavigatingToTask] = useState(false);
	const [intervals, setIntervals] = useState([]);
	const [roles, setRoles] = useState([]);

	const dispatch = useDispatch();
	const { application } = getLocalStorageData("me");
	const showAssetReferences = application?.showAssetReferences || false;
	const feedbackRequireDepartment = application?.feedbackRequireDepartment || false;

	useEffect(() => {
		const fetchDepartments = async () => {
			const response = await getSiteDepartmentsInService(siteId);
			if (response.status) setDepartments(response.data);
			else dispatch(showError("Could not fetch Departments"));
		};
		fetchDepartments();
	}, [siteId, dispatch]);

	const handleDropdownChange = async (value, type) => {
		setSelectedDropdown((prev) => ({
			...prev,
			[type]: value,
		}));

		if ((type === "position" || type === "department") && !value.id)
			setSelectedDropdown((prev) => ({ ...prev, user: {} }));
		if (type === "model")
			setSelectedDropdown((prev) => ({
				...prev,
				model: {
					...prev.model,
					name: `${value.name} ${value.modelName ? value.modelName : ""}`,
					id: value.id,
					modelTemplateType: value.modelTemplateType,
					activeModelVersionID: value.activeModelVersionID,
				},
				asset: {},
			}));
		if (type === "interval")
			setSelectedDropdown((prev) => ({ ...prev, role: {} }));
		if (type === "task")
			setSelectedDropdown((prev) => ({
				...prev,
				task: {
					...value,
					name: `${value.actionName ?? ""} ${value.name ?? ""}`.trim(),
				},
			}));

		let payload = [
			{
				path: dropdownIdNames[type],
				op: "replace",
				value: value.id,
			},
		];
		if (type === "position" || type === "department") {
			payload = [
				{
					path: dropdownIdNames[type],
					op: "replace",
					value: value.id,
				},
				...(!value.id ? [{ path: "assignUserID", op: "replace", value: null }] : []),
			];
		} else if (type === "model") {
			payload = [
				{ path: dropdownIdNames[type], op: "replace", value: value.id },
				{ path: "siteAssetRegisterID", op: "replace", value: null },
				{ path: "ModelVersionStageID", op: "replace", value: null },
				{ path: "ModelVersionZoneID", op: "replace", value: null },
				{ path: "ModelVersionTaskID", op: "replace", value: null },
				{ path: "ModelVersionIntervalID", op: "replace", value: null },
				{ path: "ModelVersionRoleID", op: "replace", value: null },
			];
		} else if (type === "interval") {
			payload = [
				{ path: dropdownIdNames[type], op: "replace", value: value.id },
				{ path: "ModelVersionRoleID", op: "replace", value: null },
			];
		} else if (type === "stage") {
			payload = [
				{ path: dropdownIdNames[type], op: "replace", value: value.id },
				{ path: "ModelVersionZoneID", op: "replace", value: null },
				...(!value.id ? [{ path: "ModelVersionTaskID", op: "replace", value: null }] : []),
			];
		} else if (type === "zone") {
			payload = [
				{ path: dropdownIdNames[type], op: "replace", value: value.id },
				...(!value.id ? [{ path: "ModelVersionTaskID", op: "replace", value: null }] : []),
			];
		}

		if (type === "stage" && value.id) {
			const [patchResponse, tasksResponse] = await Promise.all([
				updateFeedback(feedbackId, payload),
				getFeedbackTasks(
					value.id,
					undefined,
					selectedDropdown?.interval?.id || details.modelVersionIntervalID || undefined,
					selectedDropdown?.role?.id || details.modelVersionRoleID || undefined
				),
			]);

			if (tasksResponse?.status) {
				const currentTaskId = selectedDropdown?.task?.id;
				if (currentTaskId && !tasksResponse.data.some((t) => t.id === currentTaskId)) {
					setSelectedDropdown((prev) => ({ ...prev, task: {} }));
					await updateFeedback(feedbackId, [{ path: "ModelVersionTaskID", op: "replace", value: null }]);
				}
			}

			if (!patchResponse?.status)
				dispatch(showError(patchResponse?.data?.detail || patchResponse?.data || "Could not update defect"));
			return;
		}

		if (type === "zone" && value.id) {
			const [patchResponse, tasksResponse] = await Promise.all([
				updateFeedback(feedbackId, payload),
				getFeedbackTasks(
					selectedDropdown?.stage?.id || details.modelVersionStageID || undefined,
					value.id,
					selectedDropdown?.interval?.id || details.modelVersionIntervalID || undefined,
					selectedDropdown?.role?.id || details.modelVersionRoleID || undefined
				),
			]);

			if (tasksResponse?.status) {
				const currentTaskId = selectedDropdown?.task?.id;
				if (currentTaskId && !tasksResponse.data.some((t) => t.id === currentTaskId)) {
					setSelectedDropdown((prev) => ({ ...prev, task: {} }));
					await updateFeedback(feedbackId, [{ path: "ModelVersionTaskID", op: "replace", value: null }]);
				}
			}

			if (!patchResponse?.status)
				dispatch(showError(patchResponse?.data?.detail || patchResponse?.data || "Could not update defect"));
			return;
		}

		if ((type === "interval" || type === "role") && value.id) {
			const newIntervalId = type === "interval"
				? value.id
				: (selectedDropdown?.interval?.id || details.modelVersionIntervalID || undefined);
			const newRoleId = type === "role"
				? value.id
				: (selectedDropdown?.role?.id || details.modelVersionRoleID || undefined);

			const [patchResponse, tasksResponse] = await Promise.all([
				updateFeedback(feedbackId, payload),
				getFeedbackTasks(
					selectedDropdown.stage?.id,
					selectedDropdown.zone?.id,
					newIntervalId,
					newRoleId
				),
			]);

			if (tasksResponse?.status) {
				const currentTaskId = selectedDropdown?.task?.id;
				if (currentTaskId && !tasksResponse.data.some((t) => t.id === currentTaskId)) {
					setSelectedDropdown((prev) => ({ ...prev, task: {} }));
					await updateFeedback(feedbackId, [{ path: "ModelVersionTaskID", op: "replace", value: null }]);
				}
			}

			if (!patchResponse?.status)
				dispatch(showError(patchResponse?.data?.detail || patchResponse?.data || "Could not update defect"));
			return;
		}

		if (type === "position" || type === "department") {
			if (!value.id) {
				setSelectedDropdown((prev) => ({ ...prev, user: {} }));
				const remainingDeptId = type === "department"
					? undefined
					: (selectedDropdown?.department?.id || details.siteDepartmentID || undefined);
				const remainingPosId = type === "position"
					? undefined
					: (selectedDropdown?.position?.id || details.assignPositionID || undefined);
				if (remainingDeptId || remainingPosId) {
					const usersResponse = await getFeedbackAvailableUsers({
						siteDepartmentID: remainingDeptId,
						positionID: remainingPosId,
					});
					if (usersResponse?.status)
						setPositionUsers(usersResponse.data.map((d) => ({ id: d.userID, name: d.displayName, siteDepartmentID: d.siteDepartmentID })));
				} else {
					setPositionUsers([]);
				}
				// fall through to generic updateFeedback (payload already nulls assignUserID)
			} else {
				const newDeptId = type === "department"
					? value.id
					: (selectedDropdown?.department?.id || details.siteDepartmentID || undefined);
				const newPosId = type === "position"
					? value.id
					: (selectedDropdown?.position?.id || details.assignPositionID || undefined);

				const [patchResponse, usersResponse] = await Promise.all([
					updateFeedback(feedbackId, payload),
					getFeedbackAvailableUsers({
						siteDepartmentID: newDeptId,
						positionID: newPosId,
					}),
				]);

				if (usersResponse?.status) {
					const newDatas = usersResponse.data.map((d) => ({ id: d.userID, name: d.displayName, siteDepartmentID: d.siteDepartmentID }));
					setPositionUsers(newDatas);

					const currentUserId = selectedDropdown?.user?.id;
					if (!isReadOnly && newDatas.length === 1 && currentUserId !== newDatas[0].id) {
						const selectedUser = newDatas[0];
						const dropdownUpdates = { user: selectedUser };
						const patches = [{ path: "assignUserID", op: "replace", value: selectedUser.id }];
						if (selectedUser.siteDepartmentID && selectedDropdown?.department?.id !== selectedUser.siteDepartmentID) {
							const dept = departments.find((d) => d.id === selectedUser.siteDepartmentID);
							if (dept) {
								dropdownUpdates.department = dept;
								patches.push({ path: "siteDepartmentID", op: "replace", value: dept.id });
							}
						}
						setSelectedDropdown((prev) => ({ ...prev, ...dropdownUpdates }));
						await updateFeedback(feedbackId, patches);
					} else if (currentUserId && !newDatas.some((u) => u.id === currentUserId)) {
						setSelectedDropdown((prev) => ({ ...prev, user: {} }));
						await updateFeedback(feedbackId, [{ path: "assignUserID", op: "replace", value: null }]);
					}
				}

				if (!patchResponse?.status)
					dispatch(showError(patchResponse?.data?.detail || patchResponse?.data || "Could not update defect"));
				return;
			}
		}

		let finalPayload = payload;
		if (type === "user" && !isReadOnly && value.siteDepartmentID) {
			const currentDeptId = selectedDropdown?.department?.id;
			if (currentDeptId !== value.siteDepartmentID) {
				const dept = departments.find((d) => d.id === value.siteDepartmentID);
				if (dept) {
					setSelectedDropdown((prev) => ({ ...prev, department: dept }));
					finalPayload = [...payload, { path: "siteDepartmentID", op: "replace", value: dept.id }];
				}
			}
		}

		const response = await updateFeedback(feedbackId, finalPayload);

		if (!response.status)
			dispatch(
				showError(
					response?.data?.detail || response.data || "Could not update defect"
				)
			);
	};

	const handleInputChange = (e) => {
		setIsInputChanged(true);
		setInput({ ...input, [e.target.name]: e.target.value });
	};

	const handleUpdateInput = async (e) => {
		if (!isInputChanged) return;

		const response = await updateFeedback(feedbackId, [
			{
				path: e.target.name,
				op: "replace",
				value: input[e.target.name],
			},
		]);

		if (!response.status)
			dispatch(
				showError(
					response?.data?.details || response?.data || "Could not update"
				)
			);

		setIsInputChanged(false);
	};

	const pageChange = async (p, prevData) => {
		const response = await getSiteAssetRegistersLookup(siteId, null, p, page.pageSize);
		if (response.status) {
			setSiteAsset([
				...prevData,
				...response.data.filter(
					(newData) => !prevData.find((prev) => newData.id === prev.id)
				),
			]);
		}
		setPage({ pageNo: p, pageSize: page.pageSize });
	};

	const handleServerSideSearch = useCallback(
		debounce(async (searchTxt) => {
			if (searchTxt) {
				const response = await getSiteAssetRegistersLookup(siteId, null, 1, 20, searchTxt);
				if (response.status) {
					setSiteAsset(response.data);
				}
			} else {
				pageChange(1, []);
			}
		}, 500),
		[]
	);

	const positionUserID = selectedDropdown?.position?.id;
	const departmentID = selectedDropdown?.department?.id;
	const modelid = selectedDropdown?.model?.id;
	const modelVersionID = selectedDropdown?.model?.activeModelVersionID;
	const intervalId = selectedDropdown?.interval?.id;

	useEffect(() => {
		if (!modelVersionID) return;
		const fetchIntervals = async () => {
			const response = await getModelIntervals(modelVersionID);
			if (response.status) setIntervals(response.data);
			else dispatch(showError("Could not fetch intervals"));
		};
		fetchIntervals();
	}, [modelVersionID]);

	useEffect(() => {
		if (!intervalId) return;
		const fetchRoles = async () => {
			const response = await getModelRolesListByInterval(intervalId);
			if (response.status) setRoles(response.data);
			else dispatch(showError("Could not fetch roles"));
		};
		fetchRoles();
	}, [intervalId]);

	useEffect(() => {
		const fetchStageHasZones = async () => {
			const response = await getModelStage(details.activeModelVersionID);
			if (response.status) {
				const match = response.data.find((s) => s.id === details.modelVersionStageID);
				if (match) {
					setSelectedDropdown((prev) => ({
						...prev,
						stage: { ...prev.stage, hasZones: match.hasZones },
					}));
				}
			} else {
				dispatch(showError("Could not fetch stage details"));
			}
		};

		if (details.modelVersionStageID && details.activeModelVersionID) {
			fetchStageHasZones();
		}
	}, [details.modelVersionStageID, details.activeModelVersionID]);

	useEffect(() => {
		const fetchModelAvailableAssest = async (id) => {
			const response = await getModelAvailableAsset(id);
			if (response.status) {
				setAvailableModel(response.data);
			} else dispatch(showError("Could not fetch Users"));
		};
		if (modelid || details?.modelID) {
			fetchModelAvailableAssest(modelid || details.modelID);
		}
	}, [modelid, dispatch, details.modelID]);

	useEffect(() => {
		const fetchAvailableUsers = async () => {
			const response = await getFeedbackAvailableUsers({
				siteDepartmentID: details.siteDepartmentID || undefined,
				positionID: details.assignPositionID || undefined,
			});

			if (response.status) {
				const newDatas = response.data.map((d) => ({
					id: d.userID,
					name: d.displayName,
					siteDepartmentID: d.siteDepartmentID,
				}));
				setPositionUsers(newDatas);
			} else dispatch(showError("Could not fetch Users"));
		};

		if (details?.assignPositionID || details?.siteDepartmentID) {
			fetchAvailableUsers();
		}
	}, [details.assignPositionID, details.siteDepartmentID]);

	useEffect(() => {
		setSelectedDropdown({
			department: {
				id: details.siteDepartmentID,
				name: details.departmentName,
			},
			position: {
				id: details.assignPositionID,
				name: details.assignPositionName,
			},
			user: {
				id: details.assignUserID,
				name: details.assignUserName,
			},
			classification: {
				id: details.feedbackClassificationID,
				name: details.feedbackClassificationName,
			},
			priority: {
				id: details.feedbackPriorityID,
				name: details.feedbackPriorityName,
			},
			asset: {
				id: details.siteAssetRegisterID,
				assetNumber: details.siteAssetName,
			},
			model: {
				id: details.modelID,
				name: details?.modelName
					? `${details?.modelName} ${details?.model ? details.model : ""}`
					: "",
				activeModelVersionID: details.activeModelVersionID,
				modelTemplateType: details.modelType,
			},
			stage: {
				id: details.modelVersionStageID,
				name: details.stageName,
			},
			zone: {
				id: details.modelVersionZoneID,
				name: details.zoneName,
			},
			task: {
				id: details.modelVersionTaskID,
				name: `${details.taskActionName ?? ""} ${details.taskName ?? ""}`.trim(),
			},
			interval: {
				id: details.modelVersionIntervalID,
				name: details.intervalName,
			},
			role: {
				id: details.modelVersionRoleID,
				name: details.roleName,
			},
		});
		setInput({
			benefit: details.benefit,
			changeRequired: details.changeRequired,
		});
	}, [details]);

	const handleViewTaskInModel = async () => {
		if (!selectedDropdown.task?.id || isNavigatingToTask) return;
		setIsNavigatingToTask(true);
		try {
			const response = await getSingleModelTask(selectedDropdown.task.id);
			if (response.status && response.data?.[0]?.modelVersionID) {
				window.open(
					`${appPath}${modelsPath}/${response.data[0].modelVersionID}${modelTask}?modelVersionTaskID=${selectedDropdown.task.id}`,
					"_blank"
				);
			} else {
				dispatch(showError("Could not retrieve task details"));
			}
		} finally {
			setIsNavigatingToTask(false);
		}
	};

	return (
		<AccordionBox
			title="Details"
			defaultExpanded
			showSafetyCritical={details.safetyCritical}
		>
			<Grid container spacing={3}>
				<Grid item xs={12} md={6}>
					<DyanamicDropdown
						isServerSide={false}
						width="100%"
						placeholder={`Select ${captions?.position}`}
						columns={[{ id: 1, name: "name" }]}
						selectedValue={selectedDropdown.position}
						onChange={(val) => {
							handleDropdownChange(val, "position");
						}}
						showClear
						selectdValueToshow="name"
						label={captions?.position}
						fetchData={() => getPositions(siteAppID)}
						isReadOnly={isReadOnly}
						autoSelectSingleOption
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<DyanamicDropdown
						dataSource={departments}
						isServerSide={false}
						width="100%"
						showClear={!feedbackRequireDepartment}
						placeholder={`Select ${captions?.department}`}
						dataHeader={[
							{
								id: 1,
								name: "Name",
							},
							{
								id: 2,
								name: `${captions?.location ?? "Location"}`,
							},
						]}
						showHeader
						columns={[
							{ id: 1, name: "name" },
							{ id: 2, name: "description" },
						]}
						selectedValue={selectedDropdown.department}
						onChange={(val) => handleDropdownChange(val, "department")}
						selectdValueToshow="name"
						label={captions?.department}
						required={feedbackRequireDepartment}
						isReadOnly={isReadOnly}
						autoSelectSingleOption
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<DyanamicDropdown
						dataSource={positionUsers}
						isServerSide={false}
						showClear
						width="100%"
						placeholder={`Select ${captions?.user}`}
						columns={[{ id: 1, name: "name" }]}
						selectedValue={selectedDropdown.user}
						onChange={(val) => handleDropdownChange(val, "user")}
						selectdValueToshow="name"
						label={captions?.user}
						isReadOnly={isReadOnly || (!selectedDropdown.position?.id && !selectedDropdown.department?.id)}
						autoSelectSingleOption
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<DyanamicDropdown
						isServerSide={false}
						width="100%"
						placeholder={`Select ${captions?.classification}`}
						columns={[{ id: 1, name: "name" }]}
						selectedValue={selectedDropdown.classification}
						onChange={(val) => handleDropdownChange(val, "classification")}
						selectdValueToshow="name"
						label={captions?.classification}
						required
						fetchData={() => getFeedbackClassifications(siteAppID)}
						isReadOnly={isReadOnly}
						autoSelectSingleOption
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<DyanamicDropdown
						isServerSide={false}
						width="100%"
						placeholder={`Select ${captions?.priority}`}
						columns={[{ id: 1, name: "name" }]}
						selectedValue={selectedDropdown.priority}
						onChange={(val) => handleDropdownChange(val, "priority")}
						selectdValueToshow="name"
						label={captions?.priority}
						required
						fetchData={() => getFeedbackPriorities(siteAppID)}
						isReadOnly={isReadOnly}
						autoSelectSingleOption
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<DyanamicDropdown
						isServerSide={false}
						width="100%"
						showClear
						placeholder={`Select ${captions?.model}`}
						columns={[
							{ id: 1, name: "name" },
							{ id: 2, name: "modelName" },
						]}
						dataHeader={[
							{ id: 1, name: "Name" },
							{ id: 2, name: "Model" },
						]}
						showHeader
						selectedValue={selectedDropdown?.model}
						onChange={(val) => {
							handleDropdownChange(val, "model");
							setSelectedDropdown((prev) => ({
								...prev,
								stage: {},
								zone: {},
								asset: {},
								task: {},
								interval: {},
								role: {},
							}));
							setIntervals([]);
							setRoles([]);
						}}
						selectdValueToshow="name"
						label={captions?.model}
						fetchData={() => getPublishedModel(siteAppID)}
						isReadOnly={isReadOnly}
						autoSelectSingleOption
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<DyanamicDropdown
						dataSource={intervals}
						isServerSide={false}
						width="100%"
						showClear
						placeholder={`Select ${captions?.interval ?? "Interval"}`}
						columns={[{ id: 1, name: "name" }]}
						selectedValue={selectedDropdown.interval}
						onChange={(val) => handleDropdownChange(val, "interval")}
						selectdValueToshow="name"
						label={captions?.interval ?? "Interval"}
						isReadOnly={isReadOnly || !selectedDropdown.model?.id}
						autoSelectSingleOption
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<DyanamicDropdown
						isServerSide={false}
						width="100%"
						showClear
						placeholder={`Select ${captions?.stage}`}
						columns={[{ id: 1, name: "name" }]}
						selectedValue={selectedDropdown.stage}
						onChange={(val) => {
							handleDropdownChange(val, "stage");
							setSelectedDropdown((prev) => ({
								...prev,
								zone: {},
								asset: {},
								...(!val.id ? { task: {} } : {}),
							}));
						}}
						selectdValueToshow="name"
						label={captions?.stage}
						isReadOnly={isReadOnly || !selectedDropdown.model?.id}
						fetchData={() =>
							getModelStage(selectedDropdown.model?.activeModelVersionID)
						}
						autoSelectSingleOption
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<DyanamicDropdown
						dataSource={roles}
						isServerSide={false}
						width="100%"
						showClear
						placeholder={`Select ${captions?.role ?? "Role"}`}
						columns={[{ id: 1, name: "name" }]}
						selectedValue={selectedDropdown.role}
						onChange={(val) => handleDropdownChange(val, "role")}
						selectdValueToshow="name"
						label={captions?.role ?? "Role"}
						isReadOnly={isReadOnly || !selectedDropdown.interval?.id}
						autoSelectSingleOption
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<DyanamicDropdown
						isServerSide={false}
						width="100%"
						showClear
						placeholder={`Select ${captions?.zone}`}
						columns={[{ id: 1, name: "name" }]}
						selectedValue={selectedDropdown.zone}
						onChange={(val) => {
							handleDropdownChange(val, "zone");
							if (!val.id) setSelectedDropdown((prev) => ({ ...prev, task: {} }));
						}}
						selectdValueToshow="name"
						label={captions?.zone}
						isReadOnly={
							isReadOnly ||
							!selectedDropdown.model?.id ||
							!selectedDropdown.stage.hasZones
						}
						fetchData={() =>
							getModelZonesList(selectedDropdown.model?.activeModelVersionID)
						}
						autoSelectSingleOption
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<SiteAssetRegisterDropdown
						dataSource={availableModel}
						isServerSide={false}
						selectedValue={selectedDropdown.asset?.id ? selectedDropdown.asset : null}
						onChange={(val) => handleDropdownChange(val, "asset")}
						onClear={() => handleDropdownChange({}, "asset")}
						customCaptions={captions}
						label={captions?.asset}
						isReadOnly={isReadOnly}
						showReferenceColumn={showAssetReferences}
						showSelectedReference={false}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<DyanamicDropdown
						key={`task-${selectedDropdown.stage?.id ?? "none"}-${selectedDropdown.zone?.id ?? "none"}-${selectedDropdown.interval?.id ?? "none"}-${selectedDropdown.role?.id ?? "none"}`}
						isServerSide={false}
						width="100%"
						showClear
						placeholder={`Select ${captions?.task ?? "Task"}`}
						dataHeader={[
							{ id: 1, name: "Action" },
							{ id: 2, name: "Task" },
						]}
						showHeader
						columns={[
							{ id: 1, name: "actionName" },
							{ id: 2, name: "name" },
						]}
						selectedValue={selectedDropdown.task}
						onChange={(val) => handleDropdownChange(val, "task")}
						selectdValueToshow="name"
						label={captions?.task ?? "Task"}
						isReadOnly={isReadOnly || !selectedDropdown.stage?.id}
						fetchData={() =>
							getFeedbackTasks(
								selectedDropdown.stage?.id,
								selectedDropdown.zone?.id,
								selectedDropdown.interval?.id,
								selectedDropdown.role?.id
							)
						}
						autoSelectSingleOption
					/>
					{selectedDropdown.task?.id && (
						<span
							onClick={handleViewTaskInModel}
							style={{
								cursor: isNavigatingToTask ? "wait" : "pointer",
								color: "#307ad6",
								fontSize: 12,
								display: "inline-flex",
								alignItems: "center",
								gap: 4,
								marginTop: 4,
								opacity: isNavigatingToTask ? 0.6 : 1,
							}}
						>
							<OpenInNewIcon style={{ fontSize: 14 }} />
							View {captions?.task ?? "Task"} in Model
						</span>
					)}
				</Grid>
				<Grid item xs={12} md={6}>
					<Add.InputLabel>
						Change Required <Add.RequiredStar>*</Add.RequiredStar>
					</Add.InputLabel>

					<TextAreaInputField
						minRows={9}
						style={{ width: "100%" }}
						value={input?.changeRequired}
						onChange={handleInputChange}
						name="changeRequired"
						onBlur={handleUpdateInput}
						disabled={isReadOnly}
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<Add.InputLabel>
						Benefit <Add.RequiredStar>*</Add.RequiredStar>
					</Add.InputLabel>

					<TextAreaInputField
						minRows={9}
						style={{ width: "100%" }}
						value={input?.benefit}
						onChange={handleInputChange}
						name="benefit"
						onBlur={handleUpdateInput}
						disabled={isReadOnly}
					/>
				</Grid>
			</Grid>
		</AccordionBox>
	);
}

export default Details;
